import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Home, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { textsApi } from '../../utils/api';

function errorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (error?.code === 'ECONNABORTED') return 'The request took too long. Check your library before retrying.';
  return 'This action could not be completed. Please try again.';
}

export const LessonCompleteActions = ({ sourceTextId, languageId, series, onNavigate, onBusyChange }) => {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const next = series?.next_text;

  const run = async (action, operation) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(action);
    setError('');
    onBusyChange(true);
    try {
      const id = await operation();
      if (!id) throw new Error('No lesson returned');
      if (mounted.current) { onBusyChange(false); onNavigate(`/reader/${encodeURIComponent(id)}`); }
    } catch (err) {
      if (mounted.current) setError(errorMessage(err));
    } finally {
      inFlight.current = false;
      if (mounted.current) { setBusy(null); onBusyChange(false); }
    }
  };

  const generate = () => run('generate', async () => {
    const { data } = await textsApi.generateFollowUp(sourceTextId, languageId);
    return data.text_id;
  });
  const openNext = () => run('series', async () => {
    if (next.kind === 'text') return next.text_id;
    const { data } = next.kind === 'episode'
      ? await textsApi.openSeriesEpisode(next.series_id, next.episode_id)
      : await textsApi.openForgeEpisode(next.language_code, next.episode);
    return data.text_id;
  });

  return <div className="space-y-3" data-testid="lesson-complete-actions">
    {error && <p className="text-sm text-destructive break-words" role="alert" data-testid="finish-action-error">{error}</p>}
    {busy && <p className="text-xs text-muted-foreground" role="status" aria-live="polite" data-testid="finish-action-status">{busy === 'generate' ? 'Generating your follow-up lesson…' : 'Opening the next text…'}</p>}
    <div className="flex flex-col gap-2">
      <Button variant="outline" disabled={!!busy} className="w-full gap-2" onClick={() => onNavigate('/dashboard')} data-testid="finish-go-dashboard"><Home className="h-4 w-4" />Go to Dashboard</Button>
      <Button disabled={!!busy || !languageId} className="w-full gap-2 whitespace-normal h-auto min-h-10 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700" onClick={generate} title="About 250 words; up to 50 weak vocabulary words in the same language; at least 10 eligible words required" data-testid="finish-new-lesson">
        {busy === 'generate' ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Sparkles className="h-4 w-4 shrink-0" />}Generate Follow-up Lesson
      </Button>
      {series?.in_series && <>
        <Button variant="outline" disabled={!!busy || !next} className="w-full gap-2 whitespace-normal h-auto min-h-10 py-2" onClick={openNext} title={next?.title || 'No next text is available in this series'} data-testid="finish-next-series">
          {busy === 'series' ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <ArrowRight className="h-4 w-4 shrink-0" />}Next Text in Series
        </Button>
        <p className="text-xs text-muted-foreground break-words" data-testid="finish-series-next-title">{next?.title || 'No next text is available in this series.'}</p>
      </>}
    </div>
  </div>;
};